import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader, Button, Icon } from "semantic-ui-react";
import { EnvironmentalObjetivesform, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { fillTemplate } from "../../../../utils/renderTemplate";
import { fetchBaselineYearsByAspect, getBaselineYearForKpi } from "../../../../utils/dataAndFormulas/kpiBaselineYear";
import { exportVisualImage } from "../../../../utils/exportVisualImage";
import { WheelVisual } from "./WheelVisual";
import { PillarsVisual } from "./PillarsVisual";
import { MosaicVisual } from "./MosaicVisual";
import { RouteVisual } from "./RouteVisual";
import { RoadmapGantt } from "./RoadmapGantt";
import { RoadmapLanes } from "./RoadmapLanes";
import { RoadmapMilestones } from "./RoadmapMilestones";
import "./PlanDocument.scss";

const environmentalObjetivesFormController = new EnvironmentalObjetivesform();
const siteController = new Site();

const DOC_DISENOS = {
  rueda: WheelVisual,
  pilares: PillarsVisual,
  mosaico: MosaicVisual,
  ruta: RouteVisual,
};
const ROADMAP_DISENOS = {
  gantt: RoadmapGantt,
  carriles: RoadmapLanes,
  hitos: RoadmapMilestones,
};

// Mismo piso que ObjectivesMatrix.js/StrategicPlan.js si el sitio todavía no
// configuró un horizonte propio (Fase 4).
const MIN_HORIZON = 5;

// Año de línea base de un objetivo puntual — mismo criterio que
// StrategicPlan.js/ObjectivesMatrix.js (duplicado a propósito, es la
// convención ya establecida en este módulo: cada componente tiene su copia
// chica en vez de un import compartido).
function getBaseYearForObjetivo(o, formByAspect) {
  return o.baselineYear ?? getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) ?? (o.year || 0) - 1;
}

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
function formatLongDate(date) {
  return `${date.getDate()} de ${MONTHS_ES[date.getMonth()]} de ${date.getFullYear()}`;
}
function formatShortDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
}

const SITE_FIELDS = [
  { key: "name", labelKey: "pd_field_name", type: "text" },
  { key: "address", labelKey: "pd_field_address", type: "text" },
  { key: "sector", labelKey: "pd_field_sector", type: "text" },
  { key: "employees", labelKey: "pd_field_employees", type: "text" },
  { key: "referenceFramework", labelKey: "pd_field_framework", type: "text" },
];

export function PlanDocument({ siteSelected }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [objectives, setObjectives] = useState([]);
  const [formByAspect, setFormByAspect] = useState({});
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [docDiseno, setDocDiseno] = useState("rueda");
  const [roadmapDiseno, setRoadmapDiseno] = useState("gantt");
  const visualWrapRef = useRef(null);
  const roadmapWrapRef = useRef(null);

  const load = async () => {
    if (!siteSelected || !accessToken) return;
    setLoading(true);
    try {
      const [objRes, siteRes] = await Promise.all([
        environmentalObjetivesFormController.getEnvironmentalObjetivesFormsBySite(accessToken, siteSelected),
        siteController.getSite(accessToken, siteSelected),
      ]);
      const objs = objRes.code === 200 ? objRes.objetives || [] : [];
      setObjectives(objs);
      setSite(siteRes || {});
      const formByAspectResult = await fetchBaselineYearsByAspect(
        accessToken,
        siteSelected,
        objs.filter((o) => o.active !== false).map((o) => o.aspect)
      );
      setFormByAspect(formByAspectResult);
    } catch (error) {
      console.error(error);
      setObjectives([]);
      setSite({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [siteSelected, accessToken]);

  const saveField = async (field, value) => {
    setSite((prev) => ({ ...prev, [field]: value }));
    setSavingField(field);
    try {
      await siteController.updateSite(accessToken, siteSelected, { [field]: value });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingField(null);
    }
  };

  const activeObjectives = useMemo(() => objectives.filter((o) => o.active !== false), [objectives]);

  const baseYear = useMemo(() => {
    if (activeObjectives.length === 0) return new Date().getFullYear();
    return Math.min(...activeObjectives.map((o) => getBaseYearForObjetivo(o, formByAspect)));
  }, [activeObjectives, formByAspect]);

  const horizonEnd = site?.horizonYear || baseYear + MIN_HORIZON;

  const totalMetas = activeObjectives.reduce((s, o) => s + (o.metas || []).length, 0);
  const totalInvestment = activeObjectives.reduce(
    (s, o) => s + (o.metas || []).reduce((s2, m) => s2 + (parseInt((m.investment || "").replace(/[^\d]/g, ""), 10) || 0), 0),
    0
  );

  // Objetivos agrupados por aspecto — orden de primera aparición, mismo
  // patrón que StrategicPlan.js. Acá no hace falta derivar el aspecto de un
  // KPI: los objetivos sin KPI (Fase 4) ya traen el suyo propio.
  const groups = useMemo(() => {
    const order = [];
    const byAspect = {};
    activeObjectives.forEach((o) => {
      const asp = o.aspect || "—";
      if (!byAspect[asp]) { byAspect[asp] = []; order.push(asp); }
      byAspect[asp].push(o);
    });
    return order.map((aspect) => ({
      aspect,
      objetivos: byAspect[aspect],
      avgReduction: byAspect[aspect].reduce((s, o) => s + (Number(o.reductionTons) || 0), 0) / byAspect[aspect].length,
    }));
  }, [activeObjectives]);

  if (loading || site === null) return <Loader active inline="centered" />;

  const today = formatLongDate(new Date());
  const DocVisual = DOC_DISENOS[docDiseno];
  const RoadmapVisual = ROADMAP_DISENOS[roadmapDiseno];

  return (
    <div className="pd-doc">
      <div className="pd-toolbar noprint">
        <p>{t("pd_toolbar_hint")}</p>
        <Button type="button" primary onClick={() => window.print()}>
          <Icon name="print" /> {t("pd_btn_download")}
        </Button>
      </div>

      {/* ── Datos de fábrica (editable, no sale en el PDF) ── */}
      <div className="pd-card noprint">
        <div className="pd-card__title">{t("pd_factory_data_title")}</div>
        <div className="pd-factory-grid">
          {SITE_FIELDS.map((f) => (
            <div className="pd-field" key={f.key}>
              <label>{t(f.labelKey)}</label>
              <input
                type="text"
                defaultValue={site?.[f.key] || ""}
                onBlur={(e) => e.target.value !== (site?.[f.key] || "") && saveField(f.key, e.target.value)}
              />
              {savingField === f.key && <span className="pd-field__saving">{t("saving")}</span>}
            </div>
          ))}
        </div>
        <div className="pd-field pd-field--wide">
          <label>{t("pd_field_strategic_goal")}</label>
          <textarea
            defaultValue={site?.strategicGoal || ""}
            placeholder={t("pd_field_strategic_goal_placeholder")}
            onBlur={(e) => e.target.value !== (site?.strategicGoal || "") && saveField("strategicGoal", e.target.value)}
          />
          {savingField === "strategicGoal" && <span className="pd-field__saving">{t("saving")}</span>}
        </div>
      </div>

      {/* ── Documento ── */}
      <div className="pd-sheet">
        <div className="pd-brand">
          <span className="pd-brand__logo">SUSTEMIA</span>
          <span className="pd-brand__meta">{t("pd_doc_subtitle_kind")}<br />{fillTemplate(t("pd_doc_issued_template"), { date: today })}</span>
        </div>
        <div className="pd-h1">{fillTemplate(t("pd_doc_title_template"), { site: site?.name || "—" })}</div>
        <div className="pd-sub">
          {fillTemplate(t("pd_doc_summary_template"), {
            start: baseYear, end: horizonEnd, objCount: activeObjectives.length, metaCount: totalMetas,
          })}
          {" — "}{t("pd_doc_no_indicators_note")}
        </div>

        <div className="pd-asp">{t("pd_factory_data_title")}</div>
        <div className="pd-factory-readonly">
          <div><span>{t("pd_field_name")}:</span> <strong>{site?.name || "—"}</strong></div>
          <div><span>{t("pd_field_address")}:</span> {site?.address || "—"}</div>
          <div><span>{t("pd_field_sector")}:</span> {site?.sector || "—"}</div>
          <div><span>{t("pd_field_employees")}:</span> {site?.employees || "—"}</div>
          <div><span>{t("pd_doc_baseline_year")}:</span> {baseYear}</div>
          <div><span>{t("pd_doc_horizon")}:</span> {baseYear}–{horizonEnd}</div>
          <div><span>{t("pd_field_framework")}:</span> {site?.referenceFramework || "—"}</div>
          <div><span>{t("pd_doc_objectives_goals")}:</span> {activeObjectives.length} · {totalMetas}</div>
        </div>

        <div className="pd-asp">{t("pd_doc_methodology_title")}</div>
        <div className="pd-methodology">{t("pd_doc_methodology_body")}</div>

        {site?.strategicGoal && (
          <>
            <div className="pd-asp">{t("pd_field_strategic_goal")}</div>
            <div className="pd-global">{site.strategicGoal}</div>
          </>
        )}

        {activeObjectives.length > 0 && (
          <>
            <div className="pd-diseno-bar noprint">
              <span className="pd-diseno-bar__label">{t("pd_diseno_label")}</span>
              {Object.keys(DOC_DISENOS).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`pd-diseno-chip${docDiseno === key ? " on" : ""}`}
                  onClick={() => setDocDiseno(key)}
                >
                  {t(`pd_diseno_${key}`)}
                </button>
              ))}
            </div>
            <div className="pd-visual-wrap" ref={visualWrapRef}>
              <DocVisual groups={groups} baseYear={baseYear} horizonEnd={horizonEnd} siteName={site?.name} />
            </div>
            <div className="pd-visual-actions noprint">
              <button type="button" onClick={() => exportVisualImage(visualWrapRef.current, "png", "estrategia")}>{t("pd_btn_png")}</button>
              <button type="button" onClick={() => exportVisualImage(visualWrapRef.current, "jpg", "estrategia")}>{t("pd_btn_jpg")}</button>
            </div>

            <div className="pd-asp pd-asp--between">
              <span>{t("pd_roadmap_title")}</span>
              <span className="noprint pd-diseno-bar pd-diseno-bar--inline">
                {Object.keys(ROADMAP_DISENOS).map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`pd-diseno-chip${roadmapDiseno === key ? " on" : ""}`}
                    onClick={() => setRoadmapDiseno(key)}
                  >
                    {t(`pd_roadmap_${key}`)}
                  </button>
                ))}
                <button type="button" onClick={() => exportVisualImage(roadmapWrapRef.current, "png", "mapa_de_ruta")}>{t("pd_btn_png")}</button>
                <button type="button" onClick={() => exportVisualImage(roadmapWrapRef.current, "jpg", "mapa_de_ruta")}>{t("pd_btn_jpg")}</button>
              </span>
            </div>
            <div className="pd-roadmap-hint">{t("pd_roadmap_hint")}</div>
            <div className="pd-visual-wrap" ref={roadmapWrapRef}>
              <RoadmapVisual groups={groups} baseYear={baseYear} horizonEnd={horizonEnd} />
            </div>
          </>
        )}

        {activeObjectives.length === 0 ? (
          <p className="pd-empty">{t("pd_doc_empty")}</p>
        ) : (
          groups.map(({ aspect, objetivos, avgReduction }) => (
            <React.Fragment key={aspect}>
              <div className="pd-asp">
                {fillTemplate(t("pd_doc_aspect_band_template"), { aspect: t(aspect), pct: avgReduction.toFixed(1).replace(".", ",") })}
              </div>
              {objetivos.map((o) => (
                <div className="pd-obj" key={o._id}>
                  <div className="pd-obj__h">
                    {fillTemplate(t("pd_doc_obj_h_template"), {
                      code: o.code || "—", action: t(o.action), source: o.source, pct: o.reductionTons, year: o.year,
                    })}
                  </div>
                  <div className="pd-obj__meta">
                    {o.kpiCode
                      ? fillTemplate(t("pd_doc_obj_base_template"), { base: t(o.baseMedicion) })
                      : fillTemplate(t("pd_doc_obj_custom_indicator_template"), { source: o.source })}
                    {" · "}
                    {fillTemplate(t("pd_doc_obj_start_template"), { date: formatShortDate(o.periodStart) })}
                    {" · "}
                    {fillTemplate(t("pd_doc_obj_responsible_template"), { responsible: o.responsible || "—" })}
                    {" · "}
                    {fillTemplate(t("pd_doc_obj_frequency_template"), { frequency: o.frequency ? t(o.frequency) : "—" })}
                  </div>
                  {(o.metas || []).map((m) => (
                    <div className="pd-meta-item" key={m._id}>
                      <strong>{m.code}</strong> · {m.description}
                      <div className="pd-meta-item__sub">
                        {fillTemplate(t("pd_doc_meta_program_template"), {
                          start: formatShortDate(m.fechaInicio), end: formatShortDate(m.fechaFin),
                          frequency: m.frecuencia, count: (m.ocurrencias || []).length,
                        })}
                        {" · "}
                        {fillTemplate(t("pd_doc_obj_responsible_template"), { responsible: m.responsible || "—" })}
                        {" · "}
                        {fillTemplate(t("pd_doc_meta_investment_template"), { investment: m.investment || "—" })}
                        {m.roi && m.roi !== "—" && ` · ${fillTemplate(t("pd_doc_meta_roi_template"), { roi: m.roi })}`}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))
        )}

        <div className="pd-tot">
          <span>{fillTemplate(t("pd_doc_total_investment_template"), { total: totalInvestment.toLocaleString("es-AR") })}</span>
          <span>{t("pd_doc_generated_by")}</span>
        </div>
        <div className="pd-firma">
          <div>{t("pd_doc_signature_management")}</div>
          <div>{t("pd_doc_signature_sustainability")}</div>
        </div>
      </div>
    </div>
  );
}
